/*
 * sp-reviews-list.js — layout list de la sección de reseñas (snippets/sp-reviews-list.liquid; CONTRACTS §C.1, §K.3-K.5).
 * Port del modelo "Fotos y lista": filtros por estrellas y "con fotos", "Mostrar más" de a tandas y visor de foto y reseña.
 *
 * window.spReviewsList = { init(root), destroy(root), get(root), initAll(scope), destroyAll(scope) } — una instancia por
 * raíz .sp-reviews--list (WeakMap), con auto-init y soporte del editor de temas (shopify:section:load/unload y
 * shopify:block:select muestra la reseña elegida aunque esté en una tanda oculta). Es el único global del archivo.
 *
 * - Filtros: botones [data-sp-filter] con aria-pressed; cada filtro vuelve a la primera tanda (data-page-size).
 * - "Mostrar más": suma una tanda y lleva el foco a la primera reseña nueva (el botón puede desaparecer).
 * - Visor: <dialog> con showModal (top layer: ningún transform de un ancestro rompe el position: fixed, por eso no se
 *   mueve al body). Lo abren la tira de fotos ([data-sp-open="<índice>"]) y la miniatura de cada reseña. Copia el
 *   textContent de la reseña (cita, nombre, lugar, sellos y nota) y arma las fotos con data-photo-N / data-photo-N-lg
 *   (resueltas en Liquid). Nombre accesible: data-dialog-label con [name]; alt de las fotos: data-photo-alt con [name].
 *   Cierre: botón ×, click en el fondo (backdrop) y Esc nativo; al cerrar, el foco vuelve al botón que lo abrió.
 * - Navegación entre reseñas (2026-10-14): recorre las reseñas con foto en el orden de la tira (o, sin tira, las que
 *   tienen data-photo="true"), en ronda. En desktop con las flechas [data-sp-nav] y ← →; en mobile deslizando: el cuadro
 *   sigue al dedo sólo en horizontal y, al soltar pasado el umbral, sale y entra la otra (touchstart/move/end/cancel
 *   PASIVOS en el visor; si el dedo arranca sobre fotos que se desplazan, mueve las fotos). Con una sola reseña con foto
 *   el visor no lleva is-nav y las flechas no se ven. En mobile, con el visor abierto no se mueve nada más que el texto:
 *   la página queda fija y el CSS (touch-action / overscroll-behavior) anula el resto, sin listeners que bloqueen.
 * - Ningún otro listener de gesto ni de scroll y sin animaciones que el JS espere: con prefers-reduced-motion todo
 *   funciona igual (el scroll al bloque elegido en el editor no es suave).
 */
(function () {
  'use strict';
  if (window.spReviewsList && window.spReviewsList.__kit) return;

  var ROOT = '.sp-reviews--list[data-section-id]';
  var instances = new WeakMap();

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }
  function outside(dlg, e) {
    if (e.target !== dlg) return false;
    if (!e.clientX && !e.clientY) return false; // click de teclado sobre el propio dialog
    var r = dlg.getBoundingClientRect();
    return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
  }
  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function List(root) {
    this.root = root;
    this.items = Array.prototype.slice.call(root.querySelectorAll('.sp-reviews__item'));
    this.pageSize = parseInt(root.getAttribute('data-page-size'), 10) || 6;
    this.shown = this.pageSize;
    this.filter = 'all';
    this.more = root.querySelector('.sp-reviews__more');
    this.empty = root.querySelector('.sp-reviews__filter-empty');
    this.viewer = root.querySelector('.sp-reviews__viewer');
    this.opener = null;
    this.seq = [];
    this.pos = 0;
    this.touch = null;
    this.swapTimer = 0;
    this.lockY = null;
    this.bound = [];
  }

  List.prototype.on = function (target, type, fn, options) {
    if (!target) return;
    target.addEventListener(type, fn, options);
    this.bound.push([target, type, fn, options]);
  };

  List.prototype.start = function () {
    var self = this;
    this.on(this.root, 'click', function (e) {
      var f = closest(e.target, '[data-sp-filter]');
      if (f && self.root.contains(f)) {
        self.setFilter(f.getAttribute('data-sp-filter'));
        return;
      }
      if (closest(e.target, '[data-sp-more]')) {
        self.showMore();
        return;
      }
      var o = closest(e.target, '[data-sp-open]');
      if (o && self.root.contains(o)) self.open(o);
    });
    // editor de temas: el bloque elegido se muestra aunque esté en una tanda oculta o fuera del filtro
    this.on(this.root, 'shopify:block:select', function (e) {
      self.reveal(closest(e.target, '.sp-reviews__item'));
    });
    var dlg = this.viewer;
    // guard propio del visor: sin <dialog> no hay visor, pero los filtros y las tandas siguen andando
    if (dlg && typeof dlg.showModal === 'function') {
      this.on(dlg, 'click', function (e) {
        if (closest(e.target, '[data-sp-close]') || outside(dlg, e)) {
          dlg.close();
          return;
        }
        var nav = closest(e.target, '[data-sp-nav]');
        if (nav) self.go(parseInt(nav.getAttribute('data-sp-nav'), 10) || 1);
      });
      this.on(dlg, 'keydown', function (e) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') self.go(e.key === 'ArrowLeft' ? -1 : 1);
      });
      // deslizar en mobile: el cuadro sigue al dedo SÓLO en horizontal (lo vertical es el scroll del texto: touch-action
      // pan-y en el CSS); al soltar pasado el umbral sale para ese lado y entra la otra reseña desde el opuesto, si no
      // vuelve. Listeners pasivos (nunca preventDefault). Si el dedo arranca sobre fotos que se desplazan, mueve las fotos.
      this.on(dlg, 'touchstart', function (e) {
        var t = e.touches.length === 1 ? e.touches[0] : null;
        var media = closest(e.target, '.sp-reviews__vw-media');
        var photos = !!(media && media.scrollWidth > media.clientWidth + 1);
        self.touch = t && self.seq.length > 1 && !photos ? { x: t.clientX, y: t.clientY, dx: 0, axis: '' } : null;
      }, { passive: true });
      this.on(dlg, 'touchmove', function (e) {
        var s = self.touch;
        var t = e.touches[0];
        if (!s || !t || e.touches.length !== 1) return;
        var dx = t.clientX - s.x;
        var dy = t.clientY - s.y;
        if (!s.axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) s.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (s.axis !== 'x') return;
        s.dx = dx;
        self.drag(dx);
      }, { passive: true });
      this.on(dlg, 'touchend', function () {
        var s = self.touch;
        self.touch = null;
        if (s && s.axis === 'x') self.release(s.dx);
      }, { passive: true });
      this.on(dlg, 'touchcancel', function () {
        var s = self.touch;
        self.touch = null;
        if (s && s.axis === 'x') self.release(0);
      }, { passive: true });
      this.on(dlg, 'close', function () {
        clearTimeout(self.swapTimer);
        self.clearDrag();
        dlg.classList.remove('is-step');
        self.lockScroll(false);
        self.restoreFocus();
      });
    }
  };

  List.prototype.matches = function (item) {
    if (this.filter === 'all') return true;
    if (this.filter === 'photo') return item.getAttribute('data-photo') === 'true';
    return item.getAttribute('data-rating') === this.filter;
  };

  /** Muestra las this.shown primeras reseñas del filtro. → la primera que pasó a verse desde prevShown (o null). */
  List.prototype.apply = function (prevShown) {
    var matching = 0;
    var firstNew = null;
    for (var i = 0; i < this.items.length; i++) {
      var item = this.items[i];
      var show = false;
      if (this.matches(item)) {
        show = matching < this.shown;
        if (show && prevShown !== undefined && matching >= prevShown && !firstNew) firstNew = item;
        matching++;
      }
      item.hidden = !show;
    }
    if (this.more) this.more.hidden = this.shown >= matching;
    if (this.empty) this.empty.hidden = matching > 0;
    return firstNew;
  };

  List.prototype.setFilter = function (value) {
    this.filter = value || 'all';
    this.shown = this.pageSize;
    var btns = this.root.querySelectorAll('[data-sp-filter]');
    for (var i = 0; i < btns.length; i++) btns[i].setAttribute('aria-pressed', btns[i].getAttribute('data-sp-filter') === this.filter ? 'true' : 'false');
    this.apply();
  };

  List.prototype.showMore = function () {
    var prev = this.shown;
    this.shown += this.pageSize;
    var first = this.apply(prev);
    if (first) {
      first.setAttribute('tabindex', '-1');
      first.focus();
    }
  };

  List.prototype.reveal = function (item) {
    if (!item || !item.hidden) return;
    var pos = this.items.indexOf(item);
    if (pos < 0) return;
    this.setFilter('all');
    this.shown = Math.ceil((pos + 1) / this.pageSize) * this.pageSize;
    this.apply();
    if (item.scrollIntoView) item.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  List.prototype.itemFor = function (btn) {
    var idx = btn.getAttribute('data-sp-open');
    if (!idx) return closest(btn, '.sp-reviews__item');
    for (var i = 0; i < this.items.length; i++) if (this.items[i].getAttribute('data-index') === idx) return this.items[i];
    return null;
  };

  /** Las reseñas que recorre el visor: las de la tira de fotos en su orden; sin tira, las que tienen foto. */
  List.prototype.sequence = function () {
    var out = [];
    var strip = this.root.querySelectorAll('.sp-reviews__strip [data-sp-open]');
    for (var i = 0; i < strip.length; i++) {
      var it = this.itemFor(strip[i]);
      if (it && out.indexOf(it) < 0) out.push(it);
    }
    if (!out.length) for (var j = 0; j < this.items.length; j++) if (this.items[j].getAttribute('data-photo') === 'true') out.push(this.items[j]);
    return out;
  };

  List.prototype.open = function (btn) {
    var dlg = this.viewer;
    if (!dlg || typeof dlg.showModal !== 'function' || dlg.open) return;
    var item = this.itemFor(btn);
    if (!item) return;
    this.seq = this.sequence();
    this.pos = this.seq.indexOf(item);
    if (this.pos < 0) {
      this.seq = [item];
      this.pos = 0;
    }
    dlg.classList.toggle('is-nav', this.seq.length > 1);
    this.fill(item);
    this.opener = btn;
    this.lockScroll(true);
    dlg.showModal();
    var body = dlg.querySelector('.sp-reviews__vw-body');
    if (body) body.scrollTop = 0;
  };

  /** Pasa a la reseña anterior (-1) o siguiente (1) de la secuencia, en ronda; quiet = sin el fundido (al deslizar). */
  List.prototype.go = function (step, quiet) {
    var dlg = this.viewer;
    var n = this.seq.length;
    if (!dlg || !dlg.open || n < 2) return;
    this.pos = (this.pos + step + n) % n;
    this.fill(this.seq[this.pos]);
    var body = dlg.querySelector('.sp-reviews__vw-body');
    if (body) body.scrollTop = 0;
    dlg.classList.remove('is-step');
    if (quiet) return;
    // el cambio se nota: la reseña nueva entra con un fundido corto (sólo opacity, CSS; sin él con reduced motion)
    void dlg.offsetWidth;
    dlg.classList.add('is-step');
  };

  /** El cuadro sigue al dedo en horizontal (sólo transform, sin layout). */
  List.prototype.drag = function (dx) {
    var dlg = this.viewer;
    clearTimeout(this.swapTimer);
    dlg.style.transition = 'none';
    dlg.style.transform = 'translateX(' + dx + 'px)'; // opaco mientras se arrastra: no se ve la página a través
  };

  List.prototype.clearDrag = function () {
    var dlg = this.viewer;
    if (!dlg) return;
    dlg.style.transition = '';
    dlg.style.transform = '';
    dlg.style.opacity = '';
  };

  /** Al soltar: pasado el umbral sale para ese lado y entra la otra reseña desde el opuesto; si no, vuelve a su lugar. */
  List.prototype.release = function (dx) {
    var self = this;
    var dlg = this.viewer;
    var w = dlg.offsetWidth || window.innerWidth;
    var reduced = reducedMotion();
    if (Math.abs(dx) < Math.max(60, w * 0.2)) {
      dlg.style.transition = reduced ? 'none' : 'transform .2s ease-out, opacity .2s ease-out';
      dlg.style.transform = '';
      dlg.style.opacity = '';
      return;
    }
    var dir = dx < 0 ? 1 : -1; // a la izquierda: la siguiente
    if (reduced) {
      this.clearDrag();
      this.go(dir, true);
      return;
    }
    dlg.style.transition = 'transform .16s ease-in, opacity .16s ease-in';
    dlg.style.transform = 'translateX(' + (-dir * w) + 'px)';
    dlg.style.opacity = '0';
    clearTimeout(this.swapTimer);
    this.swapTimer = setTimeout(function () {
      if (!dlg.open) {
        self.clearDrag();
        return;
      }
      self.go(dir, true);
      dlg.style.transition = 'none';
      dlg.style.transform = 'translateX(' + (dir * w * 0.5) + 'px)';
      void dlg.offsetWidth;
      dlg.style.transition = 'transform .2s ease-out, opacity .2s ease-out';
      dlg.style.transform = '';
      dlg.style.opacity = '';
    }, 160);
  };

  /**
   * Mobile: con el visor abierto no se mueve nada más que el texto de la reseña (pedido del dueño del 2026-10-14). El body
   * queda fijo en su lugar (iOS ignora overflow: hidden) y html lleva sp-reviews-locked; el resto lo hace el CSS sin
   * listeners que bloqueen (PAR-39): el visor y su fondo con touch-action none, el texto pan-y sin encadenarse a la página,
   * las fotos pan-x, y la página sin rebote (overscroll-behavior none).
   */
  List.prototype.lockScroll = function (on) {
    var de = document.documentElement;
    var b = document.body;
    if (on) {
      if (this.lockY !== null || !(window.matchMedia && window.matchMedia('(max-width: 700px)').matches)) return;
      this.lockY = window.pageYOffset || de.scrollTop || 0;
      b.style.position = 'fixed';
      b.style.top = -this.lockY + 'px';
      b.style.left = '0';
      b.style.right = '0';
      de.classList.add('sp-reviews-locked');
      return;
    }
    if (this.lockY === null) return;
    var y = this.lockY;
    this.lockY = null;
    de.classList.remove('sp-reviews-locked');
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    var sb = de.style.scrollBehavior;
    de.style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    de.style.scrollBehavior = sb;
  };

  List.prototype.fill = function (item) {
    var dlg = this.viewer;
    var root = this.root;
    var q = function (sel) { return dlg.querySelector(sel); };
    var text = function (sel) {
      var el = item.querySelector(sel);
      return el ? el.textContent.trim() : '';
    };
    var name = text('.sp-reviews__name');
    var label = q('.sp-reviews__vw-label');
    if (label) label.textContent = (root.getAttribute('data-dialog-label') || '[name]').replace('[name]', name);
    var quote = q('.sp-reviews__vw-text');
    if (quote) quote.textContent = text('.sp-reviews__text');
    var nm = q('.sp-reviews__vw-name');
    if (nm) nm.textContent = name;
    var rt = q('.sp-reviews__vw-rating');
    if (rt) rt.textContent = text('.sp-reviews__rating');
    var n = parseInt(item.getAttribute('data-rating') || '5', 10);
    var stars = dlg.querySelectorAll('.sp-reviews__vw-stars span');
    for (var i = 0; i < stars.length; i++) stars[i].classList.toggle('is-on', i < n);

    var meta = q('.sp-reviews__vw-meta');
    if (meta) {
      meta.textContent = '';
      var parts = item.querySelectorAll('.sp-reviews__place, .sp-reviews__verified, .sp-reviews__refunded');
      for (var p = 0; p < parts.length; p++) {
        if (meta.childNodes.length) meta.appendChild(document.createTextNode(' · '));
        meta.appendChild(parts[p].cloneNode(true));
      }
    }

    var media = q('.sp-reviews__vw-media');
    var count = 0;
    if (media) {
      media.textContent = '';
      var alt = (root.getAttribute('data-photo-alt') || '[name]').replace('[name]', name);
      for (var k = 1; k <= 2; k++) {
        var src = item.getAttribute('data-photo-' + k);
        if (!src) continue;
        var lg = item.getAttribute('data-photo-' + k + '-lg');
        var img = document.createElement('img');
        img.src = src;
        if (lg) {
          img.srcset = src + ' 900w, ' + lg + ' 1600w';
          img.sizes = '(max-width: 700px) 92vw, 430px';
        }
        img.alt = alt;
        img.decoding = 'async';
        img.draggable = false;
        media.appendChild(img);
        count++;
      }
      media.scrollLeft = 0;
    }
    dlg.classList.toggle('is-media', count > 0);
  };

  List.prototype.restoreFocus = function () {
    var o = this.opener;
    this.opener = null;
    if (o && o.isConnected && typeof o.focus === 'function') o.focus();
  };

  List.prototype.destroy = function () {
    this.opener = null;
    clearTimeout(this.swapTimer);
    if (this.viewer && this.viewer.open && this.viewer.close) this.viewer.close();
    this.lockScroll(false);
    for (var i = 0; i < this.bound.length; i++) {
      var b = this.bound[i];
      b[0].removeEventListener(b[1], b[2], b[3]);
    }
    this.bound.length = 0;
    instances.delete(this.root);
  };

  function rootsIn(node) {
    var out = [];
    if (!node || node.nodeType !== 1) return out;
    if (node.matches(ROOT)) out.push(node);
    var found = node.querySelectorAll(ROOT);
    for (var i = 0; i < found.length; i++) out.push(found[i]);
    return out;
  }

  var api = {
    __kit: 1,
    init: function (root) {
      if (!root || root.nodeType !== 1) return null;
      var inst = instances.get(root);
      if (inst) return inst;
      inst = new List(root);
      instances.set(root, inst);
      inst.start();
      return inst;
    },
    destroy: function (root) {
      var inst = root ? instances.get(root) : undefined;
      if (!inst) return false;
      inst.destroy();
      return true;
    },
    get: function (root) {
      return root ? instances.get(root) : undefined;
    },
    initAll: function (scope) {
      var roots = rootsIn(scope && scope.nodeType === 1 ? scope : document.documentElement);
      for (var i = 0; i < roots.length; i++) api.init(roots[i]);
    },
    destroyAll: function (scope) {
      var roots = rootsIn(scope && scope.nodeType === 1 ? scope : document.documentElement);
      for (var i = 0; i < roots.length; i++) api.destroy(roots[i]);
    }
  };
  window.spReviewsList = api;

  document.addEventListener('shopify:section:unload', function (e) { api.destroyAll(e.target); });
  document.addEventListener('shopify:section:load', function (e) { api.initAll(e.target); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { api.initAll(); });
  else api.initAll();
})();
